import * as Sentry from "@sentry/react"
import { AxiosError } from "axios"

const IGNORED_ENDPOINTS = ["/match/my-session"]

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Should be called as early as possible in the application lifecycle
 */
export const initSentry = () => {
  const dns = import.meta.env.VITE_SENTRY_DSN
  const mode = import.meta.env.VITE_NODE_ENV
  const version = import.meta.env.VITE_APP_VERSION
  // Skip initialization in development for cleaner console
  if (!dns) {
    console.log("[Sentry] DSN not configured, skipping initialization")
    return
  }

  if (!["local", "development", "staging", "production"].includes(mode)) {
    console.log("[Sentry] Mode not configured, skipping initialization")
    return
  }

  console.log("[Sentry] Mode:", mode)

  const isLocal = mode === "local"

  // Sentry.init({
  //   dsn: dns,
  //   // Setting this option to true will send default PII data to Sentry.
  //   // For example, automatic IP address collection on events
  //   sendDefaultPii: true,
  // })

  Sentry.init({
    dsn: dns,
    sendDefaultPii: true,
    environment: mode,
    release: version,

    // Integrations for enhanced error context
    integrations: [
      // Browser Tracing for performance monitoring
      Sentry.browserTracingIntegration({
        enableLongTask: true,
        enableInp: true,
        traceFetch: true,
        traceXHR: true,
        shouldCreateSpanForRequest: (url) => {
          return url.includes("/api/")
        }
      }),
      // Session Replay for debugging
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: true,
        maskAllInputs: true,
        mask: [],
        block: []
      })
    ],

    maxBreadcrumbs: 50,

    // Performance Monitoring - sample 20% of transactions
    tracesSampleRate: isLocal ? 1.0 : 0.2,

    // Session Replay
    replaysSessionSampleRate: isLocal ? 1.0 : 0.01,
    replaysOnErrorSampleRate: isLocal ? 1.0 : 1.0,

    // Filter out noisy errors
    ignoreErrors: [
      // Specific errors
      "Unexpected identifier",
      // Frontend dev specific
      "Cannot access",
      // Telegram WebApp specific
      "Unable to retrieve launch parameters from any known source."
    ],

    // Custom beforeSend hook for additional filtering/enrichment
    beforeSend(event, hint) {
      const error = hint.originalException as AxiosError

      console.error(error)

      if (error?.config?.url) {
        if (
          IGNORED_ENDPOINTS.some((endpoint) => error.config!.url === endpoint)
        ) {
          return null
        }
      }

      return event
    }

    // Custom beforeBreadcrumb hook to filter/modify breadcrumbs
    // beforeBreadcrumb(breadcrumb) {
    //   // Filter out noisy console logs
    //   if (breadcrumb.category === "console" && breadcrumb.level === "log") {
    //     return null
    //   }
    //   return breadcrumb
    // },
  })

  console.log("[Sentry] Initialized successfully")
}

/**
 * Set user context for error tracking
 * Call this after user authentication
 */
export const setSentryUser = (
  userId: string | number,
  username?: string,
  additionalData?: Record<string, string>
) => {
  Sentry.setUser({
    id: String(userId),
    username,
    ...additionalData
  })
}

/**
 * Clear user context (e.g., on logout)
 */
export const clearSentryUser = () => {
  Sentry.setUser(null)
}

/**
 * Add custom context for debugging
 */
export const setSentryContext = (
  name: string,
  data: Record<string, unknown>
) => {
  Sentry.setContext(name, data)
}

/**
 * Add a breadcrumb for additional context
 */
export const addSentryBreadcrumb = (
  message: string,
  category: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = "info"
) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level
  })
}

/**
 * Manually capture an exception
 */
export const captureException = (
  error: Error | unknown,
  context?: Record<string, unknown>
) => {
  if (context) {
    Sentry.withScope((scope) => {
      scope.setExtras(context)
      Sentry.captureException(error)
    })
  } else {
    Sentry.captureException(error)
  }
}

/**
 * Manually capture a message
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = "info"
) => {
  Sentry.captureMessage(message, level)
}

// Re-export Sentry's ErrorBoundary for convenience
export { ErrorBoundary as SentryErrorBoundary } from "@sentry/react"
